import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Trash2, Image as ImageIcon, Search } from "lucide-react";

const API = '/api';

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");
  const fileRef = useRef(null);

  const fetchAssets = () => {
    setLoading(true);
    axios.get(`${API}/assets/`).then((r) => {
      setAssets(r.data.assets);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchAssets(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", file.name);
    fd.append("kind", "IMAGE");
    fd.append("tags", "uploaded");
    try {
      await axios.post(`${API}/assets/upload`, fd);
      fetchAssets();
    } catch (err) {
      console.error("Upload failed:", err);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteAsset = async (id) => {
    await axios.delete(`${API}/assets/${id}`);
    fetchAssets();
  };

  const filtered = assets.filter((a) =>
    a.name.toLowerCase().includes(filter.toLowerCase()) ||
    (a.tags || []).some((t) => t.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div className="p-8 max-w-[1600px]" data-testid="assets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171717]">Assets</h1>
          <p className="text-sm text-[#5E5E5E] mt-1">Logos, banners, badges and other brand images</p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="file-upload-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-[#0F6CBD] text-white hover:bg-[#005A9E] rounded-sm"
            data-testid="upload-asset-btn"
          >
            <Upload size={14} className="mr-2" />
            {uploading ? "Uploading..." : "Upload Asset"}
          </Button>
        </div>
      </div>

      <div className="mt-4 relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E5E5E]" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter assets..."
          className="pl-8 border-[#E1DFDD] rounded-sm"
          data-testid="asset-filter-input"
        />
      </div>

      {loading ? (
        <p className="text-sm text-[#5E5E5E] mt-6">Loading assets...</p>
      ) : filtered.length === 0 ? (
        <div className="mt-8 text-center py-12 border border-dashed border-[#E1DFDD] rounded-sm">
          <ImageIcon size={32} className="mx-auto text-[#E1DFDD]" />
          <p className="text-sm text-[#5E5E5E] mt-3">No assets found</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6" data-testid="assets-grid">
          {filtered.map((asset) => (
            <div
              key={asset.id}
              className="bg-white border border-[#E1DFDD] rounded-sm overflow-hidden hover:border-[#0F6CBD] transition-colors group"
              data-testid={`asset-card-${asset.id}`}
            >
              <div className="aspect-square bg-[#FAFAFA] flex items-center justify-center p-3 relative">
                <img
                  src={asset.url?.startsWith("http") ? asset.url : `${API}/assets/file/${asset.id}`}
                  alt={asset.name}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <button
                  onClick={() => deleteAsset(asset.id)}
                  className="absolute top-2 right-2 p-1 bg-white/90 rounded-sm text-[#A4262C] opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-asset-${asset.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="p-3 border-t border-[#E1DFDD]">
                <p className="text-xs font-medium text-[#171717] truncate">{asset.name}</p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {(asset.tags || []).map((tag) => (
                    <span key={tag} className="text-[9px] bg-[#F3F2F1] text-[#5E5E5E] px-1 py-0.5 rounded-sm">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
